import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, Subject, Subscription, switchMap, take, tap } from 'rxjs';
import { GlobalTimerService } from "../../../../../../../../core/services/global-timer.service";
import { E_DisplayMode } from '../../../../../../../../core/enums/displays/display-mode.enum';
import { E_HomeRotativeItemType } from '../../../../../../../../core/enums/home/home-rotative-item-type.enum';
import { C_DisplayData, C_CardRotatingLayoutItem, C_CardRotatingLayoutList } from '../../../../../../../../core/models/display/domain/display.model';
import { DisplayModeService } from '../../../../../../../../core/services/display-mode.service';


/**
 * Interface for internal rotation commands
 */
interface I_RotationCommand {
  dispId: string;
  forceNext?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class SmartRotativeLayoutsService {
  private globalTimer = inject(GlobalTimerService);
  private displayService = inject(DisplayModeService);

  private displays: Map<string, C_DisplayRotativeList> = new Map();

  // The heart of the reactivity: a command stream
  private rotationTrigger$ = new Subject<I_RotationCommand>();
  private engineSub: Subscription;

  // Emits whenever the UI needs to refresh
  private changes$ = new BehaviorSubject<symbol>(Symbol());

  constructor() {
    this.engineSub = this.initRotationEngine();
  }

  ngOnDestroy() {
    this.engineSub.unsubscribe();
  }

  /**
   * Main Reactive Engine
   * Handles the orchestration of timers and state changes without blocks.
   */
  initRotationEngine(): Subscription {
    return this.rotationTrigger$.pipe(
      switchMap(command => {
        const displayData = this.displays.get(command.dispId);
        if (!displayData) return of(null);

        return displayData.isRotationPausedObservable().pipe(
          switchMap(isPaused => {
            if (isPaused && !command.forceNext) return of(null);
            return this.processRotationStep(command.dispId, displayData);
          })
        );
      })
    ).subscribe();
  }

  private processRotationStep(dispId: string, displayData: C_DisplayRotativeList): Observable<any> {
    const mode = this.displayService.getCurrenDisplayMode().getValue();
    const layouts = displayData.getLayouts(mode);

    if (layouts.length === 0) return of(null);

    // 1. Normalize the index (WITHOUT INCREMENTING YET)
    // The index must represent what we want to DISPLAY NOW.
    displayData.currentLayoutIndex = (displayData.currentLayoutIndex + layouts.length) % layouts.length;

    // 2. Notify the UI to render the layout at the current index
    this.changes$.next(Symbol());

    const currentLayout = displayData.getLayoutFromId(layouts[displayData.currentLayoutIndex]);

    // 3. Guards: If there's no layout or it's an advertisement, do not schedule the next rotation
    if (!currentLayout || currentLayout.type === E_HomeRotativeItemType.Advertisement) {
      return of(null);
    }

    if (displayData.isRotationPaused()) return of(null);
    // 4. Create the timer for the NEXT step
    const timerId = this.globalTimer.createTimerWithDuration(currentLayout.millisecondsBeforeRotating);
    return this.globalTimer.getTimerObservableById(timerId).pipe(
      take(1),
      tap(() => {
        displayData.currentLayoutIndex++;
        this.rotationTrigger$.next({ dispId });
      })
    );
  }

  // --- Public API ---

  layoutChanges(): Observable<symbol> {
    return this.changes$.asObservable();
  }

  getDisplayData(dispId: string): C_DisplayRotativeList | undefined {
    return this.displays.get(dispId);
  }

  setupHospitalFromDisplayData(data: C_DisplayData) {
    const id = data.id;
    if (!id) return;

    if (!this.displays.has(id)) {
      this.displays.set(id, new C_DisplayRotativeList());
    }

    const displayItem = this.displays.get(id)!;
    displayItem.setupLayouts(data.displayInfo.cardRotatingLayout);

    const mode = this.displayService.getCurrenDisplayMode().getValue();
    const isKiosk = mode === E_DisplayMode.KioskSection || mode === E_DisplayMode.KioskHibridSection;

    if (isKiosk) {
      displayItem.setRotationPaused(false);
      this.rotationTrigger$.next({ dispId: id });
    }
  }

  managePlayPauseInterval(displayData: C_DisplayRotativeList) {
    displayData.isRotationPausedObservable().pipe(take(1)).subscribe(isPaused => {
      const newState = !isPaused;
      displayData.setRotationPaused(newState);
      this.rotationTrigger$.next({ dispId: this.findIdByData(displayData) });
    });
  }

  nextLayout(displayData: C_DisplayRotativeList) {
    displayData.currentLayoutIndex++;
    this.rotationTrigger$.next({ dispId: this.findIdByData(displayData), forceNext: true });
  }

  previousLayout(displayData: C_DisplayRotativeList) {
    displayData.currentLayoutIndex--;
    this.rotationTrigger$.next({ dispId: this.findIdByData(displayData), forceNext: true });
  }

  changeToRotativeLayout(displayData: C_DisplayRotativeList, index: number) {
    displayData.currentLayoutIndex = index;
    this.rotationTrigger$.next({ dispId: this.findIdByData(displayData), forceNext: true });
  }

  stopRotating() {
    this.displays.forEach(d => d.stopRotation());
  }

  private findIdByData(data: C_DisplayRotativeList): string {
    return [...this.displays.entries()].find(([_, v]) => v === data)?.[0] ?? '';
  }
}

/**
 * Optimized Model Class
 */
export class C_DisplayRotativeList {
  private list: Map<string, C_CardRotatingLayoutItem> = new Map();
  currentLayoutIndex: number = 0;
  layouts: string[] = [];
  layoutsWithoutAds: string[] = [];
  private isRotationPausedSubject = new BehaviorSubject<boolean>(true);

  stopRotation() {
    this.isRotationPausedSubject.next(true);
  }

  getLayoutFromId(id: string) { return this.list.get(id); }

  isRotationPausedObservable(): Observable<boolean> { return this.isRotationPausedSubject.asObservable(); }
  isRotationPaused(): boolean { return this.isRotationPausedSubject.getValue(); }

  setRotationPaused(status: boolean) { this.isRotationPausedSubject.next(status); }

  getLayouts(mode: E_DisplayMode): string[] {
    return mode === E_DisplayMode.Default ? this.layoutsWithoutAds : this.layouts;
  }

  getCurrentLayout(mode: E_DisplayMode): C_CardRotatingLayoutItem | undefined {
    const layoutIds = this.getLayouts(mode);
    const currentId = layoutIds[this.currentLayoutIndex % layoutIds.length];
    return this.list.get(currentId);
  }

  setupLayouts(layouts: C_CardRotatingLayoutList) {
    this.list.clear();
    this.layouts = [];
    this.layoutsWithoutAds = [];

    layouts.getValues().forEach((l) => {
      if (!l.id) return;
      this.list.set(l.id, l);
      this.layouts.push(l.id);
      if (l.type !== E_HomeRotativeItemType.Advertisement) {
        this.layoutsWithoutAds.push(l.id);
      }
    });
    this.currentLayoutIndex = 0;
  }
}
